import React, { useEffect, useId, useState } from "react";
import { QR_MENU_STYLE_OPTIONS, normalizeQrMenuStyle } from "../../services/local/qrMenuStyle";
import { normalizeQrSubcategoryOrder, orderQrCategories, orderQrSubcategories } from "../../services/local/qrCategoryOrder";
import { loadLocalMenuCategories } from "../../services/local/menuStorage";
import { getBranchMenuCategories } from "../../services/supabase/menuService";
import "../../styles/QrMenuStyleEditor.css";

export default function QrMenuStyleEditor({
  branchId, useRemote, value, categoryOrder, onStyleChange, onOrderChange,
  subcategoryOrder, onSubcategoryOrderChange,
  disabled = false, primaryColor = "#2563eb",
}) {
  const panelId = useId();
  const [expanded, setExpanded] = useState(false);
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  const [attempt, setAttempt] = useState(0);
  const [load, setLoad] = useState({ branchId: null, categories: [], pending: true, error: "" });

  useEffect(() => {
    let active = true;
    setLoad({ branchId, categories: [], pending: true, error: "" });
    setExpandedSubcategories({});
    const read = async () => {
      try {
        const categories = !branchId ? [] : useRemote
          ? await getBranchMenuCategories(branchId, { includeSubcategories: true })
          : loadLocalMenuCategories(branchId, { includeSubcategories: true });
        if (active) setLoad({ branchId, categories, pending: false, error: "" });
      } catch (error) {
        if (active) setLoad({ branchId, categories: [], pending: false,
          error: error.message || "No pudimos cargar las categorias." });
      }
    };
    read();
    return () => { active = false; };
  }, [branchId, useRemote, attempt]);

  const current = load.branchId === branchId;
  const categories = orderQrCategories(current ? load.categories : [], categoryOrder);
  const names = categories.map((category) => category.nombre || category.name);
  const canOrder = categories.length >= 2 || categories.some((category) => category.subcategories?.length >= 2);
  const move = (index, offset) => {
    if (disabled || index + offset < 0 || index + offset >= categories.length) return;
    const ids = categories.map((category) => category.id);
    [ids[index], ids[index + offset]] = [ids[index + offset], ids[index]];
    onOrderChange(ids);
  };
  const moveSubcategory = (category, index, offset) => {
    const rows = orderQrSubcategories(category.subcategories || [], subcategoryOrder, category.id);
    if (disabled || !onSubcategoryOrderChange || index + offset < 0 || index + offset >= rows.length) return;
    const ids = rows.map((row) => row.id);
    [ids[index], ids[index + offset]] = [ids[index + offset], ids[index]];
    onSubcategoryOrderChange({ ...normalizeQrSubcategoryOrder(subcategoryOrder), [category.id]: ids });
  };

  return <div className="qr-style-editor" style={{ "--qr-style-color": primaryColor }}>
    {QR_MENU_STYLE_OPTIONS.map((option) => (
      <label key={option.id} className={`qr-style-choice ${normalizeQrMenuStyle(value) === option.id ? "is-selected" : ""}`}>
        <span className="qr-style-choice__label">
          <input type="radio" name="qrMenuStyle" value={option.id}
            checked={normalizeQrMenuStyle(value) === option.id} disabled={disabled}
            onChange={() => onStyleChange(option.id)} />
          <strong>{option.label}</strong>
        </span>
        <span aria-hidden="true" className={`qr-style-preview qr-style-preview--${option.id}`}>
          {(names.length ? names : ["Entradas", "Pizzas", "Bebidas", "Postres"])
            .slice(0, option.id === "grid" ? 4 : 3).map((name, index) => <span key={index}>{name}</span>)}
        </span>
        <span>{option.description}</span>
      </label>
    ))}
    <section className="qr-category-order" aria-label="Orden de categorias">
      <div className="qr-category-order__heading">
        <div><strong>Orden de las categorias</strong>
          <p>Decide que categorias y subcategorias ve primero el cliente. Se aplica a los tres estilos de esta sucursal.</p></div>
        <button type="button" aria-expanded={expanded} aria-controls={panelId}
          disabled={!canOrder} onClick={() => setExpanded(!expanded)}>
          {expanded ? "Ocultar orden" : "Ordenar categorias"}
        </button>
      </div>
      {(!current || load.pending) && <p role="status">Cargando categorias...</p>}
      {current && load.error && <div role="alert">{load.error} <button type="button"
        onClick={() => setAttempt((prev) => prev + 1)}>Reintentar</button></div>}
      {current && !load.pending && !load.error && !canOrder &&
        <p>Carga al menos dos categorias o dos subcategorias en Carta para poder ordenarlas.</p>}
      <div id={panelId} hidden={!expanded || !canOrder}>
        <ol className="qr-category-order__list" aria-label="Categorias del menu">
          {categories.map((category, index) => {
            const name = category.nombre || category.name;
            const children = orderQrSubcategories(category.subcategories || [], subcategoryOrder, category.id);
            const childrenOpen = expandedSubcategories[category.id] === true;
            const childPanelId = `${panelId}-subcategories-${index}`;
            return <li key={category.id}>
              <span className="qr-category-order__number" aria-hidden="true">{index + 1}</span>
              <strong>{name}</strong>
              <div className="qr-category-order__actions">
                <button type="button" disabled={disabled || index === 0}
                  aria-label={`Subir ${name}`} onClick={() => move(index, -1)}>Subir</button>
                <button type="button" disabled={disabled || index === categories.length - 1}
                  aria-label={`Bajar ${name}`} onClick={() => move(index, 1)}>Bajar</button>
              </div>
              <div className="qr-subcategory-order">
                {children.length ? <>
                  <button type="button" aria-expanded={childrenOpen} aria-controls={childPanelId}
                    aria-label={`${childrenOpen ? "Ocultar" : "Ordenar"} subcategorias de ${name}`}
                    onClick={() => setExpandedSubcategories((previous) => ({ ...previous, [category.id]: !childrenOpen }))}>
                    {childrenOpen ? "Ocultar subcategorias" : "Ordenar subcategorias"} ({children.length})
                  </button>
                  <div id={childPanelId} hidden={!childrenOpen}>
                    <p>Orden dentro de {name}. No modifica las otras categorias.</p>
                    <ol className="qr-category-order__list qr-subcategory-order__list" aria-label={`Subcategorias de ${name}`}>
                      {children.map((child, childIndex) => {
                        const childName = child.nombre || child.name;
                        return <li key={child.id}>
                          <span className="qr-category-order__number" aria-hidden="true">{childIndex + 1}</span>
                          <strong>{childName}</strong>
                          <div className="qr-category-order__actions">
                            <button type="button" aria-label={`Subir ${childName} en ${name}`}
                              disabled={disabled || !onSubcategoryOrderChange || childIndex === 0}
                              onClick={() => moveSubcategory(category, childIndex, -1)}>Subir</button>
                            <button type="button" aria-label={`Bajar ${childName} en ${name}`}
                              disabled={disabled || !onSubcategoryOrderChange || childIndex === children.length - 1}
                              onClick={() => moveSubcategory(category, childIndex, 1)}>Bajar</button>
                          </div>
                        </li>;
                      })}
                    </ol>
                  </div>
                </> : <p>Esta categoria no tiene subcategorias.</p>}
              </div>
            </li>;
          })}
        </ol>
        <p>Las categorias y subcategorias nuevas se agregan al final. Usa Guardar cambios para conservar este orden.</p>
      </div>
    </section>
  </div>;
}
